<template>
  <div class="cs-page">
    <!--
      ⚠️ 范围下拉（scope）放第一个：它是决定「这页数字代表什么」的总开关。
         10-06 实测 ti_you_test 有 2040 场，其中真正的新随课模块只有 4 场，
         混着 2020 场已停用体优数据 + 压测垃圾。默认只给「真实业务数据」，
         想要历史全量得自己显式切过去 —— 否则打开就是一份彻底失真的报表。
    -->
    <div class="cs-toolbar">
      <el-select v-model="query.scope" class="cs-scope" placeholder="数据范围" @change="onScopeChange">
        <el-option label="仅新随课模块" value="new"></el-option>
        <el-option label="未录完场次" value="empty"></el-option>
        <el-option label="全部（含历史）" value="all"></el-option>
      </el-select>

      <el-input v-model="query.coachName" class="cs-in" placeholder="教练姓名" clearable></el-input>

      <el-input v-model="query.campusName" class="cs-in" placeholder="校区名" clearable></el-input>

      <el-select v-model="query.classType" class="cs-in-slim" placeholder="课型" clearable>
        <el-option label="大课" :value="1"></el-option>
        <el-option label="一对一" :value="2"></el-option>
      </el-select>

      <el-date-picker
          v-model="timeRange"
          class="cs-range"
          type="daterange"
          range-separator="→"
          start-placeholder="开课起"
          end-placeholder="开课止"
          value-format="YYYY-MM-DD">
      </el-date-picker>

      <select-area @areaListId="getAreaListId"/>

      <!--
        「显示已作废」开关（2026-10-20 补）。
        ⚠️ 为什么必须有：作废只是逻辑删除，但列表/统计/详情全都带 is_delete = 0 过滤，
           不作废则已，一旦作废那条记录就从系统里彻底消失 —— 既看不到、也救不回。
           有了这个开关，误作废的场景才能闭环：勾上 → 看到置灰行 → 点「恢复」。
      -->
      <div class="cs-void-toggle">
        <el-switch v-model="query.showVoided" @change="onScopeChange"/>
        <span class="cs-void-label">显示已作废</span>
        <el-tag v-if="voidedTip" type="danger" size="mini" effect="plain">{{ voidedTip }}</el-tag>
      </div>

      <div class="cs-toolbar-actions">
        <el-button type="primary" :icon="Search" @click="doSearch">搜索</el-button>
        <el-button :icon="Refresh" @click="resetQuery">重置</el-button>
        <!--
          ⚠️ 这里**故意不加 v-permission**。
          工程的 permission.js 里 btnPermissions 是硬编码的 []，isExist 恒为 false
          → 挂了该指令的节点在 mounted 时被直接 removeChild。
          照抄 Trainee.vue 的 v-permission="['trainee:export']" 会让导出按钮**永远不显示**（存量 bug）。
          导出范围的安全边界已由后端 AreaScopeUtil.enforce() 按校区强制收窄，
          前端再加一层「不显示」既挡不住越权（后端才是唯一的权限出口），
          又让「有导出功能的页面」变成一个谎报功能的页面。
        -->
        <el-button type="primary" plain :icon="Download" @click="doExport">导出</el-button>
      </div>
    </div>

    <!-- 口径提示：只在切到非默认档时才出现，默认档不占屏幕 -->
    <div v-if="scopeTip" class="cs-tip">
      <el-icon><InfoFilled /></el-icon>
      <span>{{ scopeTip }}</span>
    </div>

    <!-- 统计卡 -->
    <div class="cs-cards">
      <div v-for="c in cards" :key="c.key" class="cs-card" :class="'is-' + c.tone">
        <div class="cs-card-label">{{ c.label }}</div>
        <div class="cs-card-value">{{ c.value }}</div>
        <div v-if="c.sub" class="cs-card-sub">{{ c.sub }}</div>
        <!-- 完整率卡带进度条，扫一眼就知道「录了多少」，不用去读数字 -->
        <div v-if="c.key === 'rate' && c.rateNum !== null" class="cs-bar">
          <div class="cs-bar-fill" :style="{ width: Math.min(100, c.rateNum) + '%' }"></div>
        </div>
      </div>
    </div>

    <!--
      ⚠️ 宽度策略（10-05 用户反馈「列表宽度不全」后修）：
         原来 10 列**全是固定 px**，合计 1052px，内容区一窄就出横向滚动条 ——
         表现是右侧「照片/详情」被切掉，看起来像表格没铺满。
         现在改为「固定列收窄 + 校区列弹性吸收剩余」：
         min-width 会让 el-table 自动分配剩余宽度，三档屏都能铺满且不出滚动条。
         注意 min-width 不要给太多列，否则窄屏下反而挤成一团。
    -->
    <el-table :data="tableData" class="cs-table"
              v-loading="loading" :row-style="{ height: '52px' }"
              :row-class-name="rowClassName">
      <el-table-column prop="testId" label="场次ID" width="80">
        <template #default="scope">
          <span class="cs-mono cs-dim">{{ scope.row.testId }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="classTimeText" label="开课时间" width="150">
        <template #default="scope">
          <span class="cs-mono">{{ scope.row.classTimeText || '—' }}</span>
          <el-tag v-if="!scope.row.classTime" type="info" size="mini"
                  class="cs-tag-gap">未填</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="coachNameText" label="教练" width="100" show-overflow-tooltip />
      <!-- 唯一弹性列：把剩余宽度全部吃在这里，保证表格右侧不留空、左侧不被挤 -->
      <el-table-column label="校区 / 模式" min-width="190" show-overflow-tooltip>
        <template #default="scope">
          <!--
            ⚠️ 10-05 起校区改为非必选：不选 = 学校模式（外校/训练队），签课不带校区。
               这里留白会让人以为是数据缺失，显示成「学校模式」才是真实语义。
          -->
          <el-tag v-if="!scope.row.campusName" type="warning" size="mini"
                  effect="dark" round>学校模式</el-tag>
          <span v-else>{{ scope.row.campusName }}</span>
        </template>
      </el-table-column>
      <el-table-column label="课型" width="84">
        <template #default="scope">
          <span class="cs-pill">{{ classTypeName(scope.row.classType) }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="studentCount" label="应录" width="76" align="center" />
      <el-table-column prop="projectCount" label="测试项" width="80" align="center" />
      <el-table-column label="有效成绩" width="96" align="center">
        <template #default="scope">
          <span :class="['cs-score', scoreTone(scope)]">{{ scope.row.validScoreCount }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="photoCount" label="照片" width="68" align="center" />
      <!--
        操作列：详情 + 作废 / 恢复（2026-10-20 补全闭环）。
        ⚠️ 「作废」只对「看起来像是重复提报」的场次用：小程序提报没有幂等，
           网络重试会造出同教练同分钟的多余场次，这些若不清理会一直算进「提报场次」。
        ⚠️ 已作废的行不再显示「详情 / 作废」，只显示「恢复」——
           对一条已作废的记录再点作废是无意义操作，白白增加误点概率。
      -->
      <el-table-column label="操作" width="130" fixed="right" align="center">
        <template #default="scope">
          <template v-if="isVoided(scope.row)">
            <el-tag type="danger" size="mini" effect="plain">已作废</el-tag>
            <el-button type="text" size="small" class="cs-restore"
                       @click="doRestore(scope.row)">恢复</el-button>
          </template>
          <template v-else>
            <el-button type="text" size="small" @click="openDetail(scope.row)">详情</el-button>
            <el-button type="text" size="small" class="cs-danger"
                       @click="doVoid(scope.row)">作废</el-button>
          </template>
        </template>
      </el-table-column>
      <template #empty>
        <!--
          ⚠️ 空态必须**指认原因**。「暂无数据」四个字在这次事故里毫无价值：
             数据库里明明有 2047 场（就是教练刚录的那堂学校模式课），
             页面却显示「暂无数据」—— 一句废话把排查方向全带偏了。
          现在按 summary 的诊断字段分流，把「被权限收窄」和「确实没有」区分开。
        -->
        <div class="cs-empty">
          <el-icon class="cs-empty-icon"><DocumentDelete /></el-icon>
          <div class="cs-empty-title">{{ emptyTitle }}</div>
          <div v-if="emptyHint" class="cs-empty-hint">{{ emptyHint }}</div>
        </div>
      </template>
    </el-table>

    <div class="pagination">
      <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="query.pageIndex"
          :page-size="query.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="query.total"
          @current-change="handlePageChange"
          @size-change="handleSizeChange">
      </el-pagination>
    </div>

    <!-- 详情弹窗：逐学员 × 逐项目 成绩 -->
    <el-dialog title="场次详情 · 逐学员成绩" draggable v-model="detailDialog" width="1000px">
      <div v-if="detailTest" class="cs-detail-head">
        <el-tag size="mini" effect="dark" round>{{ detailTest.coachNameText }}</el-tag>
        <el-tag size="mini" :type="detailTest.campusName ? 'info' : 'warning'"
                effect="plain" round>{{ detailTest.campusName || '学校模式' }}</el-tag>
        <span>开课：{{ detailTest.classTimeText || '—' }}</span>
        <span v-if="detailTest.note" class="cs-dim">备注：{{ detailTest.note }}</span>
      </div>

      <el-table :data="detailRows" border v-loading="detailLoading" size="small"
                :row-style="{ height: '40px' }" :cell-style="{ padding: 0 }">
        <el-table-column prop="stuSort" label="#" width="55" />
        <el-table-column prop="stuName" label="学员" width="120" show-overflow-tooltip />
        <el-table-column label="性别" width="65">
          <template #default="scope">{{ scope.row.stuSex || '—' }}</template>
        </el-table-column>
        <!--
          ⚠️ 成绩列按「本场实际有的测试项目」动态生成，而不是写死项目列表：
             不同教练选的测试项不一样（10-05 实测有 2 项的也有 5 项的）。
             写死列必然出现整列空白，或者更糟 —— 把没测的项目显示成 0。
        -->
        <el-table-column v-for="p in detailProjects" :key="p.projectId + '_' + p.sortOrder"
                         :label="p.projectName" width="110" align="center"
                         :header-class-name="p.orphan ? 'cs-orphan-hd' : ''">
          <template #default="scope">{{ cellOf(scope.row, p) }}</template>
        </el-table-column>
        <template #empty>
          <div class="cs-dim" style="padding: 14px">本场暂无学员名单</div>
        </template>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import SelectArea from "../../components/SelectArea";
import {
  selectClassScoreSummary,
  selectClassScorePage,
  selectClassScoreDetail,
  exportClassScoreStat,
  voidClassScoreTest,
  restoreClassScoreTest
} from "@/api/classScoreStat";
// ⚠️ 图标在 main.js 里已全局注册（for...in 逐个 app.component），
//   这里再 import 一遍是冗余的，且会让打包体积白白翻倍。直接用全局组件名即可。

export default {
  name: "ClassScoreStat",
  components: { SelectArea },
  data() {
    return {
      query: {
        // ⚠️ 默认 'new'：只统计真实的新随课模块数据。绝不默认 'all'
        // （all 含 2040 场、其中约 1045 场是历史压测垃圾，打开就是一份失真的报表）
        scope: 'new',
        areaIdList: [],
        coachId: null,
        coachName: '',
        campusName: '',
        classType: null,
        classTimeStart: null,
        classTimeEnd: null,
        /**
         * 是否显示已作废场次。⚠️ 默认 false：
         * 报表的默认视图必须是「有效数据」，作废的东西只在排障/撤销时显式调出来。
         */
        showVoided: false,
        pageIndex: 1,
        pageSize: 10,
        total: 0
      },
      timeRange: null,
      loading: false,
      tableData: [],
      /** 统计卡原始数据。由 fetchList 填充，cards computed 消费。 */
      summary: {},
      detailDialog: false,
      detailLoading: false,
      detailRows: [],
      detailProjects: [],
      /**
       * 详情单元格索引：key = `${stuId}_${projectId}_${sortOrder}`，value = 展示文本。
       * ⚠️ 为什么要预先建索引而不是在模板里 filter：
       *   学员 N 人 × 项目 M 项时，逐格 filter 是 O(N×M×N)；
       *   50 人 × 5 项 = 12500 次数组扫描，对比弹窗这种交互会明显卡。
       *   建一次 O(N×M) 索引后每格是 O(1)。
       */
      detailCellMap: {},
      detailTest: null
    };
  },
  computed: {
    /**
     * 统计卡。⚠️ 完整率取 recordRateStr —— 服务端在分母为 0 时给的是 null，
     * 这种情况必须显示「—」而不是 0%（0% 会被读成「录了但全录错」）。
     */
    cards() {
      const s = this.summary || {};
      const rate = this.parseRate(s.recordRateStr);
      return [
        { key: 'session', tone: 'violet', label: '提报场次', value: this.orDash(s.sessionCount), sub: s.campusCount ? `覆盖 ${s.campusCount} 个校区` : '' },
        { key: 'coach', tone: 'cyan', label: '覆盖教练', value: this.orDash(s.coachCount), sub: '' },
        { key: 'stu', tone: 'amber', label: '覆盖学员', value: this.orDash(s.studentCountSum), sub: '' },
        {
          key: 'rate', tone: 'lime', label: '录报完整率',
          value: this.orDash(s.recordRateStr), rateNum: rate,
          sub: s.expectedScoreCount ? `已录 ${s.validScoreCount} / 应录 ${s.expectedScoreCount} 条` : ''
        }
      ];
    },
    /**
     * 当前范围的口径提示。**只在切到非默认档时出现** ——
     * 默认档（new）人人都会用，常驻一条说明纯属占屏。
     */
    scopeTip() {
      if (this.query.scope === 'empty') {
        return '「未录完场次」= 教练打开页面录了一部分就退出、没有提交的场次，用于发现「有人在用但一直录不完」。';
      }
      if (this.query.scope === 'all') {
        return '「全部」含已停用的体优历史数据与历史压测垃圾，仅供追溯，不代表当前业务量。';
      }
      return '';
    },
    /**
     * 空态标题。⚠️ 这次事故的核心教训：**空态必须指认原因**。
     * 库里明明有数据，页面却只写「暂无数据」—— 等于把排查方向全带偏。
     * 现在用 summary 的诊断字段分流，一眼区分「权限收窄」与「确实没有」。
     */
    emptyTitle() {
      if (!this.summary || !this.summary.sessionCount) {
        // visibleAreaCount 来自后端 enforce() 之后的校区集合大小
        if (this.summary && this.summary.visibleAreaCount === 0) {
          return '当前账号没有任何可见校区';
        }
        return '没有符合条件的场次';
      }
      return '暂无数据';
    },
    emptyHint() {
      if (this.summary && this.summary.visibleAreaCount === 0) {
        return '数据权限收窄到了 0 个校区，所有场次都不可见 —— 请在「系统」里配置本账号的可视校区。';
      }
      if (this.summary && this.summary.schoolModeCount > 0 && !this.summary.sessionCount) {
        return `范围内有 ${this.summary.schoolModeCount} 场学校模式记录，但未出现在列表里 —— 多半是「数据范围」档位不匹配，试试切到「全部（含历史）」。`;
      }
      return '可点「重置」清空筛选条件。';
    },
    /**
     * 已作废场次提示。只在开启「显示已作废」且确实混进来时才显示 ——
     * 否则看报表的人会以为「场次变多了」而不知道是因为含作废数据。
     */
    voidedTip() {
      if (!this.query.showVoided) return '';
      const n = this.summary && this.summary.voidedCount;
      return n > 0 ? `含 ${n} 场已作废，已计入统计` : '';
    }
  },
  created() {
    this.fetchList();
  },
  methods: {
    orDash(v) {
      return (v === null || v === undefined || v === '') ? '—' : v;
    },
    /** '86.4%' → 86.4；非法/空 → null（进度条不画，而不是画成 0%） */
    parseRate(v) {
      if (v === null || v === undefined || v === '') return null;
      const n = parseFloat(String(v).replace('%', ''));
      return isNaN(n) ? null : n;
    },
    classTypeName(t) {
      if (t === 1) return '大课';
      if (t === 2) return '一对一';
      return '—';
    },
    /**
     * 成绩着色：0 条用警示色，一眼看出「这堂课录了但没录完」；
     * 未录满用中性灰（是事实，不是错误）；录满用绿色。
     * 不用 tag 是为了在密集数字里保持可扫读性。
     */
    scoreTone(row) {
      if (!row.validScoreCount) return 'is-warn';
      if (row.projectCount && row.stuRowCount
          && row.validScoreCount < row.projectCount * row.stuRowCount) {
        return 'is-mid';
      }
      return 'is-ok';
    },
    dateText(v) {
      if (!v) return '';
      return String(v).replace('T', ' ').slice(0, 16);
    },
    doSearch() {
      // 换筛选条件必须回到第 1 页。⚠️ 忘了这行，在第 5 页改条件会得到空列表，
      // 看起来像「这个筛选没数据」。
      this.query.pageIndex = 1;
      this.fetchList();
    },
    onScopeChange() {
      this.query.pageIndex = 1;
      this.fetchList();
    },
    resetQuery() {
      this.query = {
        scope: 'new', areaIdList: [], coachId: null, coachName: '', campusName: '',
        classType: null, classTimeStart: null, classTimeEnd: null,
        showVoided: false, pageIndex: 1, pageSize: 10, total: 0
      };
      this.timeRange = null;
      this.fetchList();
    },
    getAreaListId(val) {
      this.query.areaIdList = val;
    },
    handlePageChange(val) {
      this.query.pageIndex = val;
      this.fetchList();
    },
    /**
     * 改每页条数。
     *
     * <p>⚠️ 这里**必须**把 pageIndex 重置为 1，不能只改 pageSize：
     * 用户在第 8 页（pageSize=10）把条数改成 100 时，第 8 页已越界，
     * MP 分页插件会返回空列表 —— 表现是「一改页容量数据就全没了」，
     * 是个非常容易被当成 bug 上报的假故障。
     */
    handleSizeChange(val) {
      this.query.pageSize = val;
      this.query.pageIndex = 1;
      this.fetchList();
    },
    fetchList() {
      // 时间区间 → 起止日期。⚠️ 传了区间后，服务端会把 class_time 为 NULL 的场次排除
      // （无从判断是否落在区间内）；不传则照常显示。
      if (this.timeRange && this.timeRange.length === 2) {
        this.query.classTimeStart = this.timeRange[0];
        this.query.classTimeEnd = this.timeRange[1];
      } else {
        this.query.classTimeStart = null;
        this.query.classTimeEnd = null;
      }
      this.loading = true;
      // 汇总与列表**并发**发：两者口径同源（同一 statWhere），
      // 串行会让首屏多等一个 RTT，没必要。
      Promise.all([selectClassScoreSummary(this.query), selectClassScorePage(this.query)])
        .then(([s, p]) => {
          this.summary = (s && s.code === 200) ? (s.data || {}) : {};
          if (p && p.code === 200) {
            this.tableData = (p.data.records || []).map(r => this.decorate(r));
            this.query.total = p.data.total || 0;
          } else {
            this.tableData = [];
            this.query.total = 0;
            this.$message.error((p && p.message) || '获取列表失败');
          }
        })
        .catch(() => {
          this.summary = {};
          this.tableData = [];
          this.query.total = 0;
        })
        .finally(() => { this.loading = false; });
    },
    decorate(r) {
      r.classTimeText = this.dateText(r.classTime);
      r.coachNameText = r.coachName || '（未记录）';
      // SQL 里 t.is_delete AS isDelete。⚠️ MyBatis 的 map-underscore-to-camel-case
      // 对 Map 返回类型不生效，所以这里同时兼容两种 key，避免换个驱动就判不出来。
      r.voided = this.toInt(r.isDelete != null ? r.isDelete : r.is_delete) === 1;
      return r;
    },
    toInt(v) {
      const n = parseInt(v, 10);
      return isNaN(n) ? 0 : n;
    },
    isVoided(row) {
      return !!(row && row.voided);
    },
    /** 已作废行置灰：一眼区分「这是废掉的记录」与「有效数据」 */
    rowClassName({row}) {
      return this.isVoided(row) ? 'cs-row-void' : '';
    },
    /**
     * 作废场次。
     *
     * <p>⚠️ 三重保护：① 二次确认弹窗；② 必须手输「作废」二字；③ 服务端再按校区归属校验一次。
     * 因为这个操作会让一条真实提报记录从所有统计里消失 —— 手滑点一下不能生效。</p>
     *
     * <p>⚠️ 确认框里要把「场次时间 / 教练 / 学员数」摆出来：只看场次ID 点确认，
     * 很容易废掉隔壁那条正常的场次。</p>
     */
    doVoid(row) {
      if (!row || !row.testId) {
        this.$message.error('缺少场次ID，请刷新后重试');
        return;
      }
      const info = [
        `场次ID：<b>${row.testId}</b>`,
        `教练：<b>${row.coachNameText || '（未记录）'}</b>`,
        `校区：<b>${row.campusName || '学校模式'}</b>`,
        `开课时间：<b>${row.classTimeText || '未填'}</b>`,
        `应录 ${row.studentCount || 0} 人 / 有效成绩 ${row.validScoreCount || 0} 条`,
      ].join('<br/>');
      this.$prompt(
          info + '<br/><br/>作废后该场次将从所有统计（提报场次 / 完整率 / 详情）中消失。' +
          '<br/>这是逻辑删除，库里仍可查到。<br/><br/>请输入「作废」二字确认：',
          '作废场次', {
            type: 'warning',
            confirmButtonText: '确认作废',
            cancelButtonText: '取消',
            inputPlaceholder: '请输入：作废',
            dangerouslyUseHTMLString: true,
          }
      ).then(({value}) => {
        if ((value || '').trim() !== '作废') {
          this.$message.warning('输入不匹配，已取消');
          return null;
        }
        this.loading = true;
        return voidClassScoreTest(row.testId, this.query);
      }).then(res => {
        if (res === null) return;
        if (!res || res.code !== 200) {
          this.$message.error((res && res.message) || '作废失败');
          return;
        }
        this.$message.success('已作废。可在「显示已作废」里查看并恢复');
        // 作废掉当前页最后一条时回退一页，避免停在一张空表上
        if (this.tableData.length === 1 && this.query.pageIndex > 1) {
          this.query.pageIndex -= 1;
        }
        this.fetchList();
      }).catch(err => {
        if (err !== 'cancel' && err !== 'close') this.$message.error('作废失败，请重试');
      }).finally(() => {
        this.loading = false;
      });
    },

    /**
     * 恢复已作废场次（2026-10-20 补，作废的反向操作）。
     *
     * <p>⚠️ 这里只做二次确认，<b>不要求手输文字</b>：作废是「减少数据」，恢复是「救回数据」，
     * 两者的误操作代价不对等 —— 给恢复也加手输，只会让人在慌乱时更难把数据救回来。</p>
     *
     * <p>⚠️ 确认框同样要把场次信息摆出来：列表里可能同时有多条置灰行，
     * 只看「已作废」三个字点确认，很容易恢复错隔壁那条。</p>
     */
    doRestore(row) {
      if (!row || !row.testId) {
        this.$message.error('缺少场次ID，请刷新后重试');
        return;
      }
      const info = [
        `场次ID：<b>${row.testId}</b>`,
        `教练：<b>${row.coachNameText || '（未记录）'}</b>`,
        `校区：<b>${row.campusName || '学校模式'}</b>`,
        `开课时间：<b>${row.classTimeText || '未填'}</b>`,
        `应录 ${row.studentCount || 0} 人 / 有效成绩 ${row.validScoreCount || 0} 条`,
      ].join('<br/>');
      this.$confirm(
          info + '<br/><br/>恢复后该场次会重新计入「提报场次 / 覆盖教练 / 录报完整率」，并回到详情可查状态。',
          '恢复场次', {
            type: 'info',
            confirmButtonText: '确认恢复',
            cancelButtonText: '取消',
            dangerouslyUseHTMLString: true,
          }
      ).then(() => {
        this.loading = true;
        return restoreClassScoreTest(row.testId, this.query);
      }).then(res => {
        if (!res || res.code !== 200) {
          this.$message.error((res && res.message) || '恢复失败');
          return;
        }
        this.$message.success('已恢复，统计已同步');
        this.fetchList();
      }).catch(err => {
        if (err !== 'cancel' && err !== 'close') this.$message.error('恢复失败，请重试');
      }).finally(() => {
        this.loading = false;
      });
    },

    openDetail(row) {
      this.detailDialog = true;
      this.detailLoading = true;
      this.detailRows = [];
      this.detailProjects = [];
      this.detailCellMap = {};
      this.detailTest = this.decorate({ ...row });
      selectClassScoreDetail(row.testId, this.query).then(res => {
        if (res && res.code === 200) {
          const d = res.data || {};
          this.detailTest = this.decorate({ ...(d.test || {}), testId: row.testId });
          this.detailRows = d.students || [];
          // ⚠️ 顺序：先取 rows，再用 rows 补孤儿列 —— buildProjects 需要 rows 才能发现孤儿
          this.detailProjects = this.buildProjects(d.projects, this.detailRows);
          this.detailCellMap = this.buildCellMap(this.detailRows);
        } else {
          this.$message.error((res && res.message) || '加载详情失败');
        }
      }).catch(() => { this.$message.error('加载详情失败'); })
        .finally(() => { this.detailLoading = false; });
    },
    /**
     * 组装详情列头。
     *
     * <p>⚠️ 列头**以 ti_you_test_project 为准**，但要把 ti_you_score 里
     * 「有成绩、却在 project 表里找不到对应行」的孤儿补成额外列。
     * 孤儿真实存在（2025 年老场次里 project_id=6 的孤儿成绩多达 60 行/场，
     * 典型成因是教练改了项目但旧成绩没清）。若只信 project 表，
     * 这批成绩就会**凭空消失** —— 看板上分母算了它们、单元格里却查不到，
     * 数字对不上，而且没有任何报错提示。
     *
     * <p>⚠️ 补出来的列用 projectName 兜底（可能为 null → 退化成「项目(id)」）。
     * 不猜语义、只保证「有成绩就一定有一列能显示」。
     *
     * <p>⚠️ key 必须用「projectId_sortOrder」两段，与 buildCellMap / cellOf 完全一致。
     */
    buildProjects(projects, rows) {
      const seen = new Set();
      const cols = [];
      (projects || []).forEach(p => {
        const so = (p.sortOrder === null || p.sortOrder === undefined) ? '' : p.sortOrder;
        const key = p.projectId + '_' + so;
        seen.add(key);
        cols.push({ projectId: p.projectId, sortOrder: p.sortOrder, projectName: p.projectName || ('项目' + p.projectId) });
      });
      (rows || []).forEach(r => {
        if (r.projectId === null || r.projectId === undefined) return;
        const so = (r.sortOrder === null || r.sortOrder === undefined) ? '' : r.sortOrder;
        const key = r.projectId + '_' + so;
        if (seen.has(key)) return;
        seen.add(key);
        cols.push({
          projectId: r.projectId,
          sortOrder: r.sortOrder,
          projectName: (r.projectName || ('项目' + r.projectId)) + '（无项目配置）',
          orphan: true
        });
      });
      // ⚠️ 排序必须在补完孤儿列之后做：孤儿列的 sortOrder 来自 score 表，
      //    不排序会插在列表中间，看起来像项目顺序错乱。
      cols.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      return cols;
    },
    /**
     * 建「学员 × 项目」成绩索引。
     *
     * <p>⚠️ 服务端 {@code selectScoreDetail} 返回的是**扁平行**（一个学员有成绩的
     * 每个项目一行），不是「一个学员一行、成绩内嵌」。这里负责把它们折成矩阵。
     * <p>⚠️ key 必须含 sortOrder：同一 projectId 可能有多行（同项目不同轮次/不同分道），
     * 只按 projectId 匹配会取到错的那一行 —— 这正是「50 米名次全显示 1」那类 bug 的同源问题。
     * <p>⚠️ 空成绩不写入索引 → cellOf 拿到 undefined → 显示「—」。
     * 请假/免测的学员在名单里但没有成绩行，显示「—」才是真实语义，
     * 显示 0 会被读成「测了 0 分」。
     */
    buildCellMap(rows) {
      const map = {};
      (rows || []).forEach(r => {
        // LEFT JOIN 出来的「无成绩」行：这些列全是 null
        if (r.projectId === null || r.projectId === undefined) return;
        const key = r.stuId + '_' + r.projectId + '_' + (r.sortOrder === null || r.sortOrder === undefined ? '' : r.sortOrder);
        const val = (r.projectScore === null || r.projectScore === undefined)
          ? '—'
          // 名次(lane 模式)不带单位；其余带 unit
          : (r.unit ? r.projectScore + r.unit : String(r.projectScore));
        map[key] = val;
      });
      return map;
    },
    /**
     * 取「某学员 × 某项目」的成绩单元格。
     * ⚠️ 与 buildCellMap 的 key 规则必须完全一致，两边改一处要同时改另一处。
     */
    cellOf(stu, project) {
      if (!stu) return '—';
      const so = (project.sortOrder === null || project.sortOrder === undefined) ? '' : project.sortOrder;
      const key = stu.stuId + '_' + project.projectId + '_' + so;
      return this.detailCellMap[key] || '—';
    },
    doExport() {
      exportClassScoreStat(this.query).then(res => {
        if (res && res.code === 200) {
          this.$message.success('导出中，右上角可查看进度！');
        } else {
          this.$message.error((res && res.message) || '导出失败');
        }
      }).catch(() => { this.$message.error('导出失败'); });
    }
  }
};
</script>

<style scoped>
/* 作废：低调但可辨的红字按钮，不用 danger 实心按钮以免误触 */
.cs-danger {
  color: #f56c6c !important;
}

/* 恢复：与「作废」形成对照，用正向色，但仍保持文字按钮的低调 */
.cs-restore {
  color: #67c23a !important;
  margin-left: 4px;
}

/* 「显示已作废」开关 */
.cs-void-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 4px;
}
.cs-void-label {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}

/*
  已作废行：整行降透明度 + 左侧红条。
  ⚠️ 不用 pointer-events: none —— 恢复按钮必须可点。
*/
.cs-table :deep(.cs-row-void) {
  opacity: .6;
}
.cs-table :deep(.cs-row-void) td:first-child {
  box-shadow: inset 3px 0 0 #f56c6c;
}

/* ============================================================
   视觉基调：深色玻璃 + 紫色渐变描边 + 等宽数字。
   ⚠️ 所有颜色/尺寸走 CSS 变量，改主题只需改 :root 这一处，
      不要在子规则里散落硬编码色值（那是最快变成「配色泥潭」的方式）。
   ============================================================ */
.cs-page {
  /* ⚠️ 浅色方案（10-05 用户反馈后从深色改回）。
     深色版栽在**对比度**上：表头文字 #b9c0e0 配 rgba(255,255,255,.045) 的底，
     在截图里几乎糊成一片，「无校区」「—」这类占位内容完全读不出来。
     这类后台报表不需要暗色调，浅色 + 克制的强调色才是「简洁美观大方」。
     强调色仍取系统主色紫（与 BattleScreen 一致），但饱和度/明度按白底重新调过。 */
  --c-bg: #f7f8fc;
  --c-panel: #ffffff;
  --c-line: #e6e8f0;
  --c-text: #2f3442;
  --c-dim: #79808f;
  --c-violet: #7c4dff;
  --c-cyan: #0ea5b7;
  --c-amber: #d98016;
  --c-lime: #16a34a;

  padding: 18px 20px 24px;
  min-height: 100%;
  background: var(--c-bg);
  color: var(--c-text);
}

/* ---------- 工具条 ---------- */
.cs-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--c-line);
  border-radius: 10px;
  background: var(--c-panel);
  box-shadow: 0 1px 2px rgba(31, 35, 48, 0.04);
}
/* 范围下拉是「总开关」，给它视觉权重，其余控件一律弱化 */
.cs-scope { width: 172px; }
.cs-in { width: 136px; }
.cs-in-slim { width: 108px; }
.cs-range { width: 300px; }
.cs-toolbar-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

/* ---------- 口径提示 ---------- */
.cs-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 7px 12px;
  font-size: 12.5px;
  color: #5b6270;
  border-left: 3px solid var(--c-cyan);
  background: #f0fbfd;
  border-radius: 0 6px 6px 0;
}

/* ---------- 统计卡 ---------- */
.cs-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 14px;
}
/* 窄屏降级为两列，避免卡片被压到读不出数字 */
@media (max-width: 1100px) {
  .cs-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
.cs-card {
  position: relative;
  padding: 14px 16px 15px;
  border: 1px solid var(--c-line);
  border-radius: 10px;
  background: var(--c-panel);
  box-shadow: 0 1px 2px rgba(31, 35, 48, 0.04);
  overflow: hidden;
}
/* 顶部一道主题色渐变亮边 —— 保留一点「精致感」，
   但宽度收到 46%，不再做整条渐变（白底上整条渐变会显得花） */
.cs-card::before {
  content: '';
  position: absolute;
  inset: 0 auto auto 0;
  width: 46%;
  height: 3px;
  border-radius: 3px;
  background: linear-gradient(90deg, var(--tone), transparent);
}
.cs-card.is-violet { --tone: var(--c-violet); }
.cs-card.is-cyan   { --tone: var(--c-cyan); }
.cs-card.is-amber  { --tone: var(--c-amber); }
.cs-card.is-lime   { --tone: var(--c-lime); }

.cs-card-label {
  font-size: 12.5px;
  color: var(--c-dim);
  letter-spacing: 0.4px;
}
.cs-card-value {
  margin-top: 6px;
  font-family: 'DIN Alternate', 'Consolas', ui-monospace, monospace;
  font-size: 30px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: 0.5px;
  color: #1f2430;
}
.cs-card-sub {
  margin-top: 4px;
  font-size: 11.5px;
  color: var(--c-dim);
}
/* 完整率进度条：扫一眼就知道录了多少 */
.cs-bar {
  margin-top: 9px;
  height: 5px;
  border-radius: 3px;
  background: #eef0f5;
  overflow: hidden;
}
.cs-bar-fill {
  height: 100%;
  border-radius: 3px;
  background: linear-gradient(90deg, var(--c-lime), #34d399);
  transition: width 0.35s ease;
}

/* ---------- 表格 ---------- */
/*
 * ⚠️ 表头是这次可读性事故的重灾区：深色版用 #b9c0e0 配近透明底，
 *    截图里整行表头几乎看不见。现在改为「浅灰底 + 深色字」，
 *    文字色必须够深（约 #454b5a 这一档），不要再用浅色字配浅色底。
 */
.cs-table {
  margin-top: 14px;
  /* 铺满容器：不加这行，el-table 在 flex/网格父级里会退化到内容宽度 */
  width: 100%;
  --el-table-border-color: var(--c-line);
  --el-table-bg-color: var(--c-panel);
  --el-table-tr-bg-color: var(--c-panel);
  --el-table-header-bg-color: #f4f6fa;
  --el-table-text-color: var(--c-text);
  --el-table-header-text-color: #454b5a;
  --el-table-row-hover-bg-color: #f5f3ff;
}

/* ---------- 文本零件 ---------- */
.cs-mono {
  font-family: 'Consolas', ui-monospace, monospace;
  letter-spacing: 0.3px;
}
.cs-dim { color: var(--c-dim); }
.cs-tag-gap { margin-left: 6px; }
.cs-pill {
  display: inline-block;
  padding: 1px 9px;
  font-size: 12px;
  line-height: 19px;
  border-radius: 999px;
  color: #5b6270;
  background: #f2f4f8;
}
.cs-score { font-weight: 700; font-family: 'Consolas', ui-monospace, monospace; }
.cs-score.is-ok   { color: var(--c-lime); }
.cs-score.is-mid  { color: var(--c-dim); font-weight: 500; }
.cs-score.is-warn { color: var(--c-amber); }

.cs-detail-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 13px;
  color: var(--c-dim);
}

/* ---------- 空态 ---------- */
.cs-empty {
  padding: 34px 20px;
  text-align: center;
}
.cs-empty-icon {
  font-size: 34px;
  color: #c3b8f5;
}
.cs-empty-title {
  margin-top: 10px;
  font-size: 14.5px;
  color: #3a4050;
}
.cs-empty-hint {
  margin-top: 6px;
  font-size: 12.5px;
  color: var(--c-dim);
}

.pagination {
  margin-top: 14px;
  text-align: right;
}
</style>

<style>
/*
 * ⚠️ 孤儿成绩列（ti_you_score 有成绩、ti_you_test_project 无对应行）用警示底色标出。
 *   必须是非 scoped 样式：Element Plus 的表头单元格是子组件内部渲染的，
 *   scoped 的属性选择器打不到它上面，写了也等于没写。
 *   （scoped 样式对 el-table 内部的失效是这个组件的通病，不是配置问题。）
 */
.cs-orphan-hd {
  background: #fdf6ec !important;
  color: #b88230 !important;
}

/*
 * ⚠️ 深色版里这里有一整段「Element Plus 暗色适配」（把输入框/下拉/日期范围
 *   的白底改成半透明白、字改成浅色）。改回浅色后**必须整段撤掉** ——
 *   它会把 Element Plus 原生控件的浅色文字改成 #e8ecff，在白底上直接看不见。
 *   浅色方案下让控件用组件库自己的默认样式即可，不要覆写。
 */
.cs-page .el-table__empty-block {
  background: transparent;
}
</style>
